import NetworkCall from "network/networkCall";
import Request from "network/request";
import {
  camelCaseKeys,
  camelCaseKeysRecursively,
} from "utilities/generalUtility";

export default class Candidate {
  static async getByEmail(email, loading = true) {
    const res = await NetworkCall.fetch(
      Request.getCandidateByEmail(email),
      loading,
    );
    return camelCaseKeys(res.data);
  }

  static async getActivity(id) {
    const res = await NetworkCall.fetch(Request.getCandidateActivity(id));
    return camelCaseKeysRecursively(res.data);
  }

  static async uploadCSV(body) {
    return NetworkCall.fetch(Request.uploadCSV(body), false);
  }

  static async create(
    values,
    { showError = true, skipForbiddenRedirect = false } = {},
  ) {
    const res = await NetworkCall.fetch(
      Request.createJobCandidate(values),
      true,
      showError,
      skipForbiddenRedirect,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async getByJobId(body) {
    const res = await NetworkCall.fetch(Request.getCandidatesByJobId(body));
    return camelCaseKeysRecursively(res.data);
  }

  static async getJobRecruiterByJobId(
    id,
    loading = true,
    { showError = true, skipForbiddenRedirect = false } = {},
  ) {
    const res = await NetworkCall.fetch(
      Request.getJobRecruiterByJobId(id),
      loading,
      showError,
      skipForbiddenRedirect,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async getCandidateDetail(id) {
    const res = await NetworkCall.fetch(Request.getCandidateDetail(id));
    return camelCaseKeysRecursively(res.data);
  }

  static async addToBlacklist(data) {
    const res = await NetworkCall.fetch(Request.addToBlacklist(data));
    return camelCaseKeys(res.data);
  }

  static async removeToBlacklist(data) {
    const res = await NetworkCall.fetch(Request.removeToBlacklist(data));
    return camelCaseKeys(res.data);
  }

  static async edit(values) {
    const res = await NetworkCall.fetch(Request.editJobCandidate(values));
    return camelCaseKeysRecursively(res.data);
  }

  static async changeStatus(values) {
    const res = await NetworkCall.fetch(Request.changeCandidateStatus(values));
    return camelCaseKeysRecursively(res.data);
  }

  static async addComment(values) {
    return NetworkCall.fetch(Request.addCommentOnCandidate(values));
  }

  static async getComments(id) {
    return NetworkCall.fetch(Request.getCandidateComments(id));
  }

  static async getLogs(id) {
    const res = await NetworkCall.fetch(Request.getCandidateLogs(id));
    return camelCaseKeysRecursively(res.data);
  }

  static async getCandidateBillings(id) {
    const res = await NetworkCall.fetch(Request.getCandidateBillingsIssues(id));
    return camelCaseKeysRecursively(res.data);
  }

  static async uploadCV(file, folder) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderName", folder);
    const res = await NetworkCall.fetch(Request.uploadFile(formData), false);
    return res.data;
  }

  static async editCandidateDetails(body) {
    const res = await NetworkCall.fetch(Request.editCandidateDetailsById(body));
    return camelCaseKeysRecursively(res.data);
  }

  static async editOfferedSalary(payload) {
    const res = await NetworkCall.fetch(
      Request.editCandidateOfferedSalary(payload),
    );
    return camelCaseKeys(res.data);
  }

  static async deleteCV(filePath) {
    const res = await NetworkCall.fetch(Request.deleteCV(filePath));
    return res;
  }

  static async suggestAnEmail(jobId) {
    const res = await NetworkCall.fetch(Request.suggestAnEmail(jobId));
    return camelCaseKeys(res.data);
  }

  static async deleteCandiate(candidateJobEntryId) {
    const res = await NetworkCall.fetch(
      Request.deleteCandiate(candidateJobEntryId),
    );
    return res;
  }

  static async transferCandiateJob(body, loader = true) {
    const res = await NetworkCall.fetch(
      Request.transferCandiateJob(body),
      loader,
      true,
      true,
    );
    return res;
  }

  static async getCandidateBilling(candidateJobEntryId, loader = true) {
    const res = await NetworkCall.fetch(
      Request.getCandidateBillings(candidateJobEntryId),
      loader,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async getByClassId(id) {
    const res = await NetworkCall.fetch(Request.getCandidatesByClassId(id));
    return camelCaseKeysRecursively(res.data);
  }

  static async assignCandidate(body) {
    const res = await NetworkCall.fetch(Request.assignCandidateClass(body));
    return camelCaseKeysRecursively(res.data);
  }

  static async detachCandidate(body) {
    const res = await NetworkCall.fetch(Request.detachCandidateClass(body));
    return camelCaseKeysRecursively(res.data);
  }

  static async getClientClassManager(body) {
    const res = await NetworkCall.fetch(Request.getClientClassManager(body));
    return camelCaseKeysRecursively(res.data);
  }

  static async changeCandidateStatusInBulk(
    body,
    { showError = true, skipForbiddenRedirect = false } = {},
  ) {
    const res = await NetworkCall.fetch(
      Request.changeCandidateStatusInBulk(body),
      true,
      showError,
      skipForbiddenRedirect,
    );
    return camelCaseKeysRecursively(res.data);
  }

  // v2

  static v2 = {
    get: async (params, useLoading = true) => {
      return NetworkCall.fetch(Request.v2.getCandidates(params), useLoading);
    },

    merge: async (data) => {
      return NetworkCall.fetch(Request.v2.mergeCandidates(data));
    },

    editEngagementNotes: async (jobId, candidateId, data) => {
      const res = await NetworkCall.fetch(
        Request.v2.editCandidateJobEntry(jobId, candidateId, data),
      );
      return camelCaseKeys(res.data);
    },

    editComment: async (id, params) => {
      return NetworkCall.fetch(Request.v2.editComment(id, params));
    },

    getAvailableTransitions: async (candidateJobEntryId) => {
      return NetworkCall.fetch(
        Request.v2.getCandidateAvailableTransitions(candidateJobEntryId),
      );
    },

    getByJobId: async (jobId, data, useLoading = true) => {
      return NetworkCall.fetch(Request.v2.getByJobId(jobId, data), useLoading);
    },

    getPipeline: async (candidateJobEntryId) => {
      return NetworkCall.fetch(
        Request.v2.getCandidatePipeline(candidateJobEntryId),
      );
    },

    getTabCounts: async (candidateJobEntryId) => {
      return NetworkCall.fetch(
        Request.v2.getCandidateJobEntryTabCounts(candidateJobEntryId),
        false,
      );
    },

    getTransitionDateFields: async (candidateJobEntryId, params) => {
      return NetworkCall.fetch(
        Request.v2.getTransitionDateFields(candidateJobEntryId, params),
        false,
        false,
      );
    },
  };
}
